import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Copy, Send, Trash2, ArrowLeftRight, GripVertical, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/pages/app/AppLayout";
import { isoDay, monday, addDays, useWorkplaces } from "@/pages/app/Ops";
import { AvailabilityPanel, SwapsPanel, OfferSwapDialog, dayKey } from "@/components/app/RosterExtras";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RosterCosts } from "@/components/app/RosterCosts";
import { RosterPrint } from "@/components/app/RosterPrint";
import { ResponseBadge, StaffShift } from "@/components/app/RosterRespond";
import { RosterEmailStatus } from "@/components/app/RosterEmailStatus";

const dayLabel = (d) => d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
const Pick = ({ value, onChange, items, placeholder, testid, className = "w-40" }) => (
  <Select value={value} onValueChange={onChange}><SelectTrigger className={`${className} rounded-xl`} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
);

const AddShift = ({ days, members, wps, onAdded }) => {
  const first = isoDay(days.find((d) => isoDay(d) >= isoDay(new Date())) || days[0]);
  const [f, setF] = useState({ membership_id: "", workplace_id: "", date: first, start: "09:00", end: "17:00", repeat_weeks: "0" });
  useEffect(() => {
    setF((x) => ({ ...x, date: first }));
  }, [first]);
  const add = async () => {
    try {
      const { data } = await api.post("/roster/shifts", { ...f, workplace_id: f.workplace_id || null, repeat_weeks: Number(f.repeat_weeks) });
      toast.success(data.created > 1 ? `${data.created} shifts added` : "Shift added");
      if (data.conflicts?.length) toast.warning(`Overlaps another shift: ${data.conflicts.map((c) => `${c.business_name} ${c.start}–${c.end}`).join(", ")}`, { duration: 8000 });
      onAdded();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="mb-5 flex flex-wrap items-end gap-2 rounded-2xl border border-slate-200 bg-white p-4" data-testid="roster-add-form">
      <Pick value={f.membership_id} onChange={(v) => setF({ ...f, membership_id: v })} items={members.map((m) => [m.membership_id, m.name])} placeholder="Employee" testid="roster-member-select" className="w-44" />
      <Pick value={f.workplace_id} onChange={(v) => setF({ ...f, workplace_id: v })} items={wps.map((w) => [w.workplace_id, w.name])} placeholder="Location" testid="roster-workplace-select" />
      <Pick value={f.date} onChange={(v) => setF({ ...f, date: v })} items={days.map((d) => [isoDay(d), dayLabel(d)])} testid="roster-day-select" />
      <Input type="time" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="w-28 rounded-xl" data-testid="roster-start" />
      <Input type="time" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="w-28 rounded-xl" data-testid="roster-end" />
      <Pick value={f.repeat_weeks} onChange={(v) => setF({ ...f, repeat_weeks: v })} items={[["0", "Just this week"], ...[1, 3, 7, 11].map((n) => [String(n), `Repeat ${n + 1} weeks`])]} testid="roster-repeat-select" />
      <Button onClick={add} disabled={!f.membership_id} className="rounded-xl bg-[#00C16B] hover:bg-[#00A85C]" data-testid="roster-add-btn"><Plus className="mr-1 h-4 w-4" />Add shift</Button>
    </div>
  );
};

const EditShiftDialog = ({ shift, days, members, wps, onClose, onSaved }) => {
  const [f, setF] = useState({ membership_id: shift.membership_id, workplace_id: shift.workplace_id || "", date: shift.date, start: shift.start, end: shift.end });
  const save = async () => {
    try { const { data } = await api.patch(`/roster/shifts/${shift.roster_shift_id}`, { ...f, workplace_id: f.workplace_id || null }); toast.success("Shift updated"); if (data.conflicts?.length) toast.warning(`Overlaps another shift: ${data.conflicts.map((c) => `${c.business_name} ${c.start}–${c.end}`).join(", ")}`, { duration: 8000 }); onSaved(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="roster-edit-dialog">
        <DialogHeader><DialogTitle>Edit shift</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Pick value={f.membership_id} onChange={(v) => setF({ ...f, membership_id: v })} items={members.map((m) => [m.membership_id, m.name])} testid="roster-edit-member" className="w-full" />
          <Pick value={f.workplace_id} onChange={(v) => setF({ ...f, workplace_id: v })} items={wps.map((w) => [w.workplace_id, w.name])} placeholder="Location" testid="roster-edit-workplace" className="w-full" />
          <Pick value={f.date} onChange={(v) => setF({ ...f, date: v })} items={days.map((d) => [isoDay(d), dayLabel(d)])} testid="roster-edit-day" className="w-full" />
          <div className="flex gap-2"><Input type="time" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="rounded-xl" data-testid="roster-edit-start" /><Input type="time" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="rounded-xl" data-testid="roster-edit-end" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-full bg-[#00C16B] hover:bg-[#00A85C]" data-testid="roster-edit-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const shiftTone = (s) => (s.leave_conflict || s.response === "declined" ? "bg-red-50 ring-1 ring-red-300" : s.response === "adjusted" ? "bg-sky-50 ring-1 ring-sky-300" : s.published ? "bg-emerald-50" : "bg-amber-50");

const ManagerGrid = ({ days, members, shifts, wps, avail, leave = [], onMove, onEdit, onDelete }) => {
  const [over, setOver] = useState(null);
  const wpName = (id) => wps.find((w) => w.workplace_id === id)?.name;
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="roster-grid">
      <table className="w-full min-w-[980px] table-fixed text-xs">
        <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><th className="w-40 px-3 py-2">Employee</th>{days.map((d) => <th key={isoDay(d)} className="px-2 py-2">{dayLabel(d)}</th>)}</tr></thead>
        <tbody>{members.map((m) => (
          <tr key={m.membership_id} className="border-t border-slate-100 align-top">
            <td className="px-3 py-2 text-sm font-medium">{m.name}</td>
            {days.map((d) => {
              const key = `${m.membership_id}|${isoDay(d)}`;
              const a = avail[m.membership_id]?.weekly?.[dayKey(d)];
              return (
                <td key={key} className={`h-20 px-1.5 py-1.5 transition-colors ${over === key ? "bg-emerald-50" : a && !a.available ? "bg-slate-100/70" : ""}`} data-testid={`roster-cell-${m.membership_id}-${isoDay(d)}`}
                  onDragOver={(e) => { e.preventDefault(); setOver(key); }} onDragLeave={() => setOver(null)}
                  onDrop={(e) => { e.preventDefault(); setOver(null); onMove(e.dataTransfer.getData("text/plain"), m.membership_id, isoDay(d)); }}>
                  {leave.some((l) => l.membership_id === m.membership_id && l.date === isoDay(d)) && <p className="mb-1 rounded bg-sky-100 px-1 text-[10px] font-semibold text-sky-800" data-testid={`roster-leave-${m.membership_id}-${isoDay(d)}`}>On leave</p>}
                  {a && !a.available && <p className="text-[10px] text-slate-400">Unavailable</p>}
                  {a?.available && a.start && <p className="text-[10px] text-slate-400">Avail {a.start}–{a.end}</p>}
                  {shifts.filter((s) => s.membership_id === m.membership_id && s.date === isoDay(d)).map((s) => (
                    <div key={s.roster_shift_id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", s.roster_shift_id)} onClick={() => onEdit(s)}
                      className={`mb-1 cursor-grab rounded-lg p-1.5 active:cursor-grabbing ${shiftTone(s)}`} data-testid={`roster-shift-${s.roster_shift_id}`}>
                      <div className="flex items-center justify-between gap-1"><span className={`font-mono-num font-semibold ${s.response === "declined" ? "line-through text-slate-400" : ""}`}>{s.start}–{s.end}</span>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(s); }} className="text-red-600" aria-label="Delete" data-testid={`roster-delete-${s.roster_shift_id}`}><Trash2 className="h-3 w-3" /></button></div>
                      <p className="truncate text-slate-500"><GripVertical className="inline h-3 w-3" />{wpName(s.workplace_id) || "Any"}{!s.published && " · draft"}{s.leave_conflict && " · on leave!"}</p>
                      {s.published && <ResponseBadge s={s} />}
                    </div>))}
                </td>);
            })}
          </tr>))}</tbody>
      </table>
    </div>
  );
};

const MyWeek = ({ days, shifts, wps, onSwap, onChanged, start }) => {
  const today = isoDay(new Date());
  const pending = shifts.filter((s) => s.date >= today && !s.response).length;
  const confirmAll = async () => {
    try { const { data } = await api.post("/roster/confirm-week", { start }); toast.success(`Confirmed ${data.confirmed} shift${data.confirmed === 1 ? "" : "s"}`); onChanged(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
  <div>
    {pending > 0 && (
      <div className="mb-3 flex flex-col gap-2 rounded-2xl border border-[#00C16B]/40 bg-[#00C16B]/5 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="roster-confirm-banner">
        <p className="text-sm font-semibold text-[#0B2D5B]">You have {pending} shift{pending > 1 ? "s" : ""} to confirm. Tap Change if you need different times, or Can't work to decline a day.</p>
        <Button onClick={confirmAll} className="h-11 rounded-full bg-[#00C16B] hover:bg-[#00A85C]" data-testid="roster-confirm-all-btn">Confirm all</Button>
      </div>
    )}
  <div className="grid gap-3 md:grid-cols-7" data-testid="roster-week">
    {days.map((d) => (
      <div key={isoDay(d)} className="min-h-[120px] rounded-2xl border border-slate-200 bg-white p-3">
        <p className="text-xs font-bold uppercase text-slate-500">{dayLabel(d)}</p>
        <div className="mt-2 space-y-2">{shifts.filter((s) => s.date === isoDay(d)).map((s) => (
          <StaffShift key={s.roster_shift_id} s={s} wpName={wps.find((w) => w.workplace_id === s.workplace_id)?.name} today={today} onSwap={onSwap} onChanged={onChanged} />))}</div>
      </div>
    ))}
  </div>
  </div>
  );
};

export default function RosterPage() {
  const { membership } = useAuth();
  const mgr = membership.role !== "employee";
  const [week, setWeek] = useState(monday());
  const [shifts, setShifts] = useState([]);
  const [members, setMembers] = useState([]);
  const [avail, setAvail] = useState({});
  const [edit, setEdit] = useState(null);
  const [swap, setSwap] = useState(null);
  const [swapKey, setSwapKey] = useState(0);
  const wps = useWorkplaces();
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const start = isoDay(week);
  const [leave, setLeave] = useState([]);
  const [costKey, setCostKey] = useState(0);
  const canCost = membership.role === "owner" || !!membership.payroll_access;
  const latest = useRef(start);
  latest.current = start;
  const load = useCallback(() => Promise.all([api.get("/roster", { params: { start } }), api.get("/leave/calendar", { params: { start } })]).then(([r, l]) => { if (latest.current !== start) return; setShifts(r.data); setLeave(l.data); setCostKey((k) => k + 1); }), [start]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (mgr) {
      api.get("/members").then((r) => setMembers(r.data.filter((m) => m.status === "active")));
      api.get("/availability").then((r) => setAvail(Object.fromEntries(r.data.map((a) => [a.membership_id, a]))));
    }
  }, [mgr]);
  const act = async (fn, msg) => { try { const { data } = await fn(); toast.success(msg(data)); load(); } catch (e) { toast.error(formatError(e)); } };
  const move = (id, membership_id, date) => {
    const s = shifts.find((x) => x.roster_shift_id === id);
    if (!s || (s.membership_id === membership_id && s.date === date)) return;
    act(() => api.patch(`/roster/shifts/${id}`, { membership_id, date }), () => "Shift moved");
  };
  return (
    <div data-testid="roster-page">
      <PageHeader eyebrow="Staff rosters" title={`Week of ${week.toLocaleDateString("en-AU", { day: "numeric", month: "long" })}`}>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => setWeek(addDays(week, -7))} data-testid="roster-prev-btn">← Prev</Button>
          <Button variant="outline" className="rounded-xl" onClick={() => setWeek(addDays(week, 7))} data-testid="roster-next-btn">Next →</Button>
          {mgr && <Button variant="outline" className="rounded-xl" onClick={() => act(() => api.post("/roster/copy-previous", { start }), (d) => `Copied ${d.copied} shifts`)} data-testid="roster-copy-btn"><Copy className="mr-1 h-4 w-4" />Copy last week</Button>}
          {mgr && <Button variant="outline" className="rounded-xl" onClick={() => window.print()} data-testid="roster-print-btn"><Printer className="mr-1 h-4 w-4" />Print</Button>}
          {mgr && <Button className="rounded-xl bg-[#0B2D5B]" onClick={() => act(() => api.post("/roster/publish", { start }), (d) => `Published ${d.published} shift${d.published === 1 ? "" : "s"}${d.notified ? ` · ${d.notified} worker${d.notified > 1 ? "s" : ""} notified in the app${d.emailed ? `, ${d.emailed} emailed` : ""}` : ""}`)} data-testid="roster-publish-btn"><Send className="mr-1 h-4 w-4" />Publish</Button>}
        </div>
      </PageHeader>
      <Tabs defaultValue="roster">
        <TabsList><TabsTrigger value="roster" data-testid="roster-tab-roster">Roster</TabsTrigger><TabsTrigger value="availability" data-testid="roster-tab-availability">Availability</TabsTrigger><TabsTrigger value="swaps" data-testid="roster-tab-swaps">Shift swaps</TabsTrigger></TabsList>
        <TabsContent value="roster" className="mt-5">
          {mgr ? <>
            {canCost && <RosterCosts start={start} refreshKey={costKey} />}
            <RosterEmailStatus start={start} refreshKey={costKey} />
            <AddShift days={days} members={members} wps={wps} onAdded={load} />
            <p className="mb-3 text-xs text-slate-500">Drag a shift to another day or person (on a phone, tap a shift to move it). Tap a shift to edit it. Drafts are amber until you publish.</p>
            <ManagerGrid days={days} members={members} shifts={shifts} wps={wps} avail={avail} leave={leave} onMove={move} onEdit={setEdit}
              onDelete={(s) => act(() => api.delete(`/roster/shifts/${s.roster_shift_id}`), () => "Shift removed")} />
          </> : <MyWeek days={days} shifts={shifts} wps={wps} onSwap={setSwap} onChanged={load} start={start} />}
        </TabsContent>
        <TabsContent value="availability" className="mt-5"><AvailabilityPanel mgr={mgr} members={members} /></TabsContent>
        <TabsContent value="swaps" className="mt-5"><SwapsPanel mgr={mgr} wps={wps} refreshKey={swapKey} onChanged={load} /></TabsContent>
      </Tabs>
      {mgr && <RosterPrint days={days} members={members} shifts={shifts} wps={wps} />}
      {edit && <EditShiftDialog shift={edit} days={days} members={members} wps={wps} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {swap && <OfferSwapDialog shift={swap} onClose={() => setSwap(null)} onSaved={() => { setSwap(null); setSwapKey((k) => k + 1); }} />}
    </div>
  );
}
